import { Divider, Stack, Theme, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import Humanize from "humanize-plus";
import type { ReactNode } from "react";
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis } from "recharts";
import { ChartTitleStyled, StyledTooltip } from "./StatusChartStyles";

/** The subset of `DownloadJournal` / `UploadJournal` this chart reads; both are assignable to it. */
interface StatusJournalEntry {
  date?: string | null;
  completed?: number | null;
  dirty_rows?: boolean | null;
  failed?: number | null;
  max_bytes?: number | null;
  max_rows?: number | null;
  min_bytes?: number | null;
  min_rows?: number | null;
  running?: number | null;
  total_bytes?: number | null;
  total_rows?: number | null;
}

interface StatusChartData {
  name?: string | null;
  status?: string | null;
  credential?: { status?: string | null } | null;
  journal: StatusJournalEntry[];
}

interface ChartTitleProps {
  assetType: string;
  name?: string | null;
  status?: string | null;
  keyStatus?: string | null;
  viewFiles: ReactNode;
}

const ChartTitle = ({ assetType, name, status, keyStatus, viewFiles }: ChartTitleProps) => {
  let suffix = "";
  const isSuspended = status === "suspended" || keyStatus === "suspended";
  if (status === "suspended") {
    suffix = `(${assetType} suspended)`;
  } else if (keyStatus === "suspended") {
    suffix = "(key suspended)";
  }
  return (
    <ChartTitleStyled isSuspended={isSuspended}>
      {name} {suffix}
      {viewFiles}
    </ChartTitleStyled>
  );
};

const getStatusColors = (theme: Theme) => ({
  COMPLETED: theme.palette.accentSuccess30.main,
  ERROR: theme.palette.accentDanger30.main,
  WORKING: theme.palette.accentInfo30.main,
  INACTIVE: theme.palette.surfaceLight50.main,
});

const formatDateTick = (v: string) => format(new Date(v.replace(/-/g, "/")), "MM/dd");

const formatNumberTick = (v: number) => (v === Math.floor(v) ? Humanize.compactInteger(v, 0) : "");

interface StatusChartProps {
  data: StatusChartData;
  prefix: string;
  viewFiles: ReactNode;
  assetType: string;
}

const StatusChart = ({ data, prefix, viewFiles, assetType }: StatusChartProps) => {
  const theme = useTheme();
  const statusColors = getStatusColors(theme);

  const chartData = data.journal.map((x) => ({
    date: x.date,
    total: x.total_rows,
    //prettier-ignore
    fill: (x.failed ?? 0) > 0 ? statusColors.ERROR :
          (x.running ?? 0) > 0 ? statusColors.WORKING :
            statusColors.COMPLETED,
    completed: x.completed,
    failed: x.failed,
    running: x.running,
    min_rows: x.min_rows,
    max_rows: x.max_rows,
    nonCompleted: x.total_rows == 0 && (x.completed ?? 0) + (x.failed ?? 0) + (x.running ?? 0) > 0 ? 20 : null,
  }));

  return (
    <>
      <ChartTitle
        name={data.name}
        status={data.status}
        viewFiles={viewFiles}
        keyStatus={data.credential?.status || ""}
        assetType={assetType}
      />
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickFormatter={formatDateTick} />
          <YAxis tickFormatter={formatNumberTick} label={{ value: "Total rows", angle: -90, position: "insideLeft" }} />
          <Scatter dataKey="nonCompleted" fill="red" />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              return (
                <StyledTooltip>
                  <Stack sx={{ gap: "20px" }}>
                    <Stack sx={{ gap: "5px" }}>
                      <Typography variant="body.small.bold">{payload[0].payload.date}:</Typography>
                      <Divider />
                      <Typography variant="body.small.bold">Rows</Typography>
                      <Typography variant="body.small.regular">
                        Total: {Humanize.intComma(payload[0].payload.total)}
                      </Typography>
                      <Typography variant="body.small.regular">
                        Range {payload[0].payload.min_rows} - {payload[0].payload.max_rows}
                      </Typography>
                    </Stack>
                    <Stack sx={{ gap: "5px" }}>
                      <Typography variant="body.small.bold">{prefix}</Typography>
                      <Typography variant="body.small.regular">Completed: {payload[0].payload.completed}</Typography>
                      <Typography variant="body.small.regular">Failed: {payload[0].payload.failed}</Typography>
                      <Typography variant="body.small.regular">Running: {payload[0].payload.running}</Typography>
                    </Stack>
                  </Stack>
                </StyledTooltip>
              );
            }}
          />
          <Bar dataKey="total" barSize={20} />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
};

export { StatusChart };
export type { StatusChartData, StatusJournalEntry };
